import React, { useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import TopNav from '../TopNav/TopNav';
import AutopickCategoryList from './pages/AutopickCategoryList';
import AutopickPartList from './pages/AutopickPartList';
import CSMTableList from './pages/CSMTableList';
import CompanyList from './pages/CompanyList';
import ConfigurationAdmin from './pages/ConfigurationAdmin';
import ContactList from './pages/ContactList';
import LookupList from './pages/LookupList';
import MultiplierList from './pages/MultiplierList';
import OrderSheetDetail from './pages/OrderSheetDetail';
import OrderSheetList from './pages/OrderSheetList';
import PartsList from './pages/PartsList';
import ProjectList from './pages/ProjectList';
import PumpTableList from './pages/PumpTableList';
import RulesList from './pages/RulesList';
import { ChangePasswordPage, ResetPasswordPage, SalesUnauthorizedPage } from './pages/SalesAuthUtilityPages';
import SalesQuoteDetail from './pages/SalesQuoteDetail';
import SalesQuoteList from './pages/SalesQuoteList';
import TermsConditionsList from './pages/TermsConditionsList';
import UnitDetail from './pages/UnitDetail';
import UnitsList from './pages/UnitsList';
import UserList from './pages/UserList';
import VTConfigurationList from './pages/VTConfigurationList';
import { getSalesValue, useSalesApiList } from './hooks/useSalesApiResource';
import { formatSalesCurrency, formatSalesDate } from './pages/salesPageUtils';
import './SalesPortalContainer.css';

const SALES_ROUTE_PREFIX = '/sales';

const withSalesPath = (path) => `${SALES_ROUTE_PREFIX}${path}`;

const normalizeDashboardQuote = (quote = {}) => ({
  id: Number(getSalesValue(quote, 'id', 'Id') || 0),
  projectId: Number(getSalesValue(quote, 'projectId', 'ProjectId') || 0),
  quotationNumber: String(getSalesValue(quote, 'quotationNumber', 'QuotationNumber') || ''),
  statusId: Number(getSalesValue(quote, 'statusId', 'StatusId') || 0),
  saleRepId: Number(getSalesValue(quote, 'saleRepId', 'SaleRepId') || 0),
  createdBy: String(getSalesValue(quote, 'createdBy', 'CreatedBy') || ''),
  createdDate: String(getSalesValue(quote, 'createdDate', 'CreatedDate') || ''),
  finalPrice: Number(getSalesValue(quote, 'finalPrice', 'FinalPrice', 'totalPrice', 'TotalPrice') || 0),
  isActive: Boolean(getSalesValue(quote, 'isActive', 'IsActive') ?? true),
});
const normalizeDashboardProject = (project = {}) => ({
  id: Number(getSalesValue(project, 'id', 'Id') || 0),
  name: String(getSalesValue(project, 'name', 'Name') || ''),
  companyId: Number(getSalesValue(project, 'companyId', 'CompanyId') || 0),
});
const normalizeDashboardCompany = (company = {}) => ({
  id: Number(getSalesValue(company, 'id', 'Id') || 0),
  name: String(getSalesValue(company, 'name', 'Name') || ''),
});
const normalizeDashboardRows = (rows) => rows;
const normalizeDashboardQuotes = (rows) => rows.map(normalizeDashboardQuote);
const normalizeDashboardProjects = (rows) => rows.map(normalizeDashboardProject);
const normalizeDashboardCompanies = (rows) => rows.map(normalizeDashboardCompany);

const SalesDashboardShell = () => {
  const { items: quotes } = useSalesApiList((endpoints) => endpoints.salesQuotes.base, { normalize: normalizeDashboardQuotes });
  const { items: projects } = useSalesApiList((endpoints) => endpoints.projects.base, { normalize: normalizeDashboardProjects });
  const { items: companies } = useSalesApiList((endpoints) => endpoints.companies.base, { normalize: normalizeDashboardCompanies });
  const { items: contacts } = useSalesApiList((endpoints) => endpoints.contacts.base, { normalize: normalizeDashboardRows });
  const { items: orders } = useSalesApiList((endpoints) => endpoints.salesQuoteOrders.base, { normalize: normalizeDashboardRows });

  const projectById = new Map(projects.map((project) => [project.id, project]));
  const companyById = new Map(companies.map((company) => [company.id, company]));
  const recentQuotes = [...quotes]
    .sort((left, right) => new Date(right.createdDate || 0) - new Date(left.createdDate || 0))
    .slice(0, 5);
  const dashboardMetrics = [
    { label: 'Projects', value: projects.length, className: 'sales-metric-blue' },
    { label: 'Active Quotes', value: quotes.filter((quote) => quote.isActive).length, className: 'sales-metric-green' },
    { label: 'Companies', value: companies.length, className: 'sales-metric-yellow' },
    { label: 'Orders', value: orders.length, className: 'sales-metric-red' },
  ];

  return (
    <section className="sales-portal-page sales-dashboard-page" aria-labelledby="sales-dashboard-title">
      <h4 id="sales-dashboard-title" className="mb-4">Dashboard</h4>
      <div className="row mb-4">
        {dashboardMetrics.map((metric) => (
          <div className="col-xl-3 col-md-6 mb-4" key={metric.label}>
            <div className={`card text-white h-100 sales-metric-card ${metric.className}`}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>{metric.label}</div>
                  <div className="sales-metric-value">{metric.value}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <i className="bi bi-table me-2"></i>
          Recent Sale Quotes
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Company Name</th>
                  <th>Quotation Number</th>
                  <th>Status</th>
                  <th>Sales Rep</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                  <th className="text-end">Final Price</th>
                </tr>
              </thead>
              <tbody>
                {recentQuotes.length === 0 ? (
                  <tr><td colSpan={8}><div className="sales-no-data">No recent sales quotes found.</div></td></tr>
                ) : recentQuotes.map((quote) => {
                  const project = projectById.get(quote.projectId);
                  const company = project ? companyById.get(project.companyId) : null;
                  return (
                    <tr key={quote.id}>
                      <td>{project?.name || '-'}</td>
                      <td>{company?.name || '-'}</td>
                      <td>{quote.quotationNumber}</td>
                      <td>{quote.statusId || '-'}</td>
                      <td>{quote.saleRepId || '-'}</td>
                      <td>{quote.createdBy}</td>
                      <td>{formatSalesDate(quote.createdDate)}</td>
                      <td className="text-end"><strong>{formatSalesCurrency(quote.finalPrice)}</strong></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="text-muted small">{contacts.length} contacts loaded from the Sales API.</div>
    </section>
  );
};

export const buildSalesNavItems = ({ isAdmin, canEditSales }) => [
  { label: 'Dashboard', icon: 'bi-house-door', path: withSalesPath('/dashboard') },
  {
    label: 'Sales',
    icon: 'bi-diagram-3',
    children: [
      { label: 'Sales Quote', icon: 'bi-file-earmark-text', path: withSalesPath('/quotes') },
      { label: 'Company', icon: 'bi-building', path: withSalesPath('/companies') },
      { label: 'Contacts', icon: 'bi-person-lines-fill', path: withSalesPath('/contacts') },
    ],
  },
  { label: 'Pump Table', icon: 'bi-gear-wide-connected', path: withSalesPath('/pumps') },
  { label: 'CSM', icon: 'bi-table', path: withSalesPath('/csm') },
  { label: 'Order Sheet', icon: 'bi-receipt-cutoff', path: withSalesPath('/order-sheets') },
  ...(canEditSales && !isAdmin ? [{ label: 'Instrument', icon: 'bi-tools', path: withSalesPath('/parts') }] : []),
  ...(isAdmin
    ? [
        { label: 'Rules', icon: 'bi-book', path: withSalesPath('/admin/rules') },
        {
          label: 'Proposal Configuration',
          icon: 'bi-gear',
          children: [
            { label: 'Terms And Conditions', icon: 'bi-file-text', path: withSalesPath('/terms-conditions') },
          ],
        },
      ]
    : []),
  ...((isAdmin || canEditSales)
    ? [
        {
          label: 'Administrator',
          icon: 'bi-building',
          children: [
            ...(isAdmin ? [
              { label: 'Instrument', icon: 'bi-tools', path: withSalesPath('/parts') },
              { label: 'Category Auto Pick', icon: 'bi-magic', path: withSalesPath('/admin/autopick-category') },
              { label: 'Parts Auto Pick', icon: 'bi-puzzle', path: withSalesPath('/admin/autopick-parts') },
            ] : []),
            { label: 'Configuration Builder', icon: 'bi-sliders2-vertical', path: withSalesPath('/admin/configuration') },
            ...(isAdmin ? [
              { label: 'VT Configuration', icon: 'bi-diagram-2', path: withSalesPath('/admin/vt-configurations') },
            ] : []),
          ],
        },
      ]
    : []),
  ...(isAdmin
    ? [
        {
          label: 'User Management',
          icon: 'bi-people-fill',
          children: [
            { label: 'Users', icon: 'bi-person-fill', path: withSalesPath('/admin/users') },
            { label: 'Reset Password', icon: 'bi-key', path: withSalesPath('/admin/reset-password') },
          ],
        },
        {
          label: 'Global Settings',
          icon: 'bi-sliders',
          children: [
            { label: 'Lookup Table', icon: 'bi-list-ul', path: withSalesPath('/admin/lookups') },
            { label: 'System Multipliers', icon: 'bi-percent', path: withSalesPath('/admin/multipliers') },
          ],
        },
      ]
    : []),
];

const SalesPortalContainer = () => {
  const location = useLocation();
  const { canView, canEdit, isAdmin, isLoading } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const canViewSales = canView('sales.container');
  const canEditSales = canEdit('sales.container');
  const navItems = buildSalesNavItems({ isAdmin: isAdmin(), canEditSales });
  const isActive = (path) => location.pathname === path;
  const isChildActive = (children = []) => children.some((child) => isActive(child.path));
  const isSectionOpen = (item) => {
    if (openSections[item.label] !== undefined) return openSections[item.label];
    return isChildActive(item.children);
  };
  const toggleSection = (label) => {
    setOpenSections((current) => ({ ...current, [label]: !current[label] }));
  };
  const closeSidebar = () => setSidebarOpen(false);

  if (isLoading) {
    return (
      <div className="sales-portal-shell">
        <section className="sales-portal-page">
          <div className="sales-loading">Loading...</div>
        </section>
      </div>
    );
  }

  if (!canViewSales) {
    return (
      <div className="sales-portal-shell">
        <section className="sales-portal-page">
          <div className="card">
            <div className="card-body">
              <h4>Access Denied</h4>
              You do not have access to Sales Portal.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <TopNav />
      <div className="sales-portal-shell">
        <div className="sales-app-layout">
          <header className="sales-app-header">
            <div className="sales-header-left">
              <button
                className="sales-menu-toggle-btn d-lg-none"
                type="button"
                onClick={() => setSidebarOpen((current) => !current)}
                aria-label="Toggle Sales menu"
              >
                <i className="bi bi-list"></i>
              </button>
              <span className="sales-app-title d-none d-md-inline">Sales Portal</span>
            </div>
          </header>

          {sidebarOpen && <div className="sales-sidebar-overlay d-lg-none" onClick={closeSidebar}></div>}
          <aside className={`sales-sidebar ${sidebarOpen ? 'open' : ''}`} aria-label="Sales Portal">
            <nav className="sales-sidebar-nav">
              <ul className="nav flex-column">
                {navItems.map((item) => (
                  <li key={item.label} className="nav-item">
                    {item.children ? (
                      <>
                        <button
                          type="button"
                          className={`nav-link sales-nav-link-collapse w-100 text-start ${isChildActive(item.children) ? 'active' : ''}`}
                          onClick={() => toggleSection(item.label)}
                        >
                          <i className={`bi ${item.icon} me-2`}></i>
                          <span>{item.label}</span>
                          <i className={`bi ms-auto ${isSectionOpen(item) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                        </button>
                        {isSectionOpen(item) && (
                          <ul className="nav flex-column sales-sidebar-submenu">
                            {item.children.map((child) => (
                              <li key={child.path} className="nav-item">
                                <Link
                                  to={child.path}
                                  className={`nav-link sales-nav-link-child ${isActive(child.path) ? 'active' : ''}`}
                                  onClick={closeSidebar}
                                >
                                  <i className={`bi ${child.icon} me-2`}></i>
                                  <span>{child.label}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                        onClick={closeSidebar}
                      >
                        <i className={`bi ${item.icon} me-2`}></i>
                        <span>{item.label}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="sales-main-content-wrapper">
            <div className="sales-main-content">
              <Routes>
                <Route index element={<Navigate to="/sales/dashboard" replace />} />
                <Route path="dashboard" element={<SalesDashboardShell />} />
                <Route path="quotes/new" element={<Navigate to="/sales/projects" replace />} />
                <Route path="projects" element={<ProjectList />} />
                <Route path="quotes" element={<SalesQuoteList />} />
                <Route path="quotes/:id" element={<SalesQuoteDetail />} />
                <Route path="salesquote/:projectId/details" element={<SalesQuoteDetail />} />
                <Route path="salesquote/:projectId/details/:id" element={<SalesQuoteDetail />} />
                <Route path="companies" element={<CompanyList />} />
                <Route path="contacts" element={<ContactList />} />
                <Route path="units" element={<UnitsList />} />
                <Route path="units/new" element={<UnitDetail />} />
                <Route path="units/:id" element={<UnitDetail />} />
                <Route path="pumps" element={<PumpTableList />} />
                <Route path="parts" element={<PartsList />} />
                <Route path="csm" element={<CSMTableList />} />
                <Route path="terms-conditions" element={<TermsConditionsList />} />
                <Route path="order-sheets" element={<OrderSheetList />} />
                <Route path="order-sheet/detail/:id" element={<OrderSheetDetail />} />
                <Route path="admin/users" element={<UserList />} />
                <Route path="admin/lookups" element={<LookupList />} />
                <Route path="admin/multipliers" element={<MultiplierList />} />
                <Route path="admin/rules" element={<RulesList />} />
                <Route path="admin/configuration" element={<ConfigurationAdmin />} />
                <Route path="admin/system-configuration" element={<Navigate to="/sales/admin/configuration?tab=stations" replace />} />
                <Route path="admin/station-type-configs" element={<Navigate to="/sales/admin/configuration?tab=structure" replace />} />
                <Route path="admin/vt-configurations" element={<VTConfigurationList />} />
                <Route path="admin/autopick-category" element={<AutopickCategoryList />} />
                <Route path="admin/autopick-parts" element={<AutopickPartList />} />
                <Route path="change-password" element={<ChangePasswordPage />} />
                <Route path="admin/reset-password" element={<ResetPasswordPage />} />
                <Route path="unauthorized" element={<SalesUnauthorizedPage />} />
                <Route path="*" element={<Navigate to="/sales/dashboard" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default SalesPortalContainer;
